import React, { useRef, useEffect, useCallback } from 'react';

const NUMBERS = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10,
  5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26,
];
const RED_NUMBERS = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);
const POCKET_COUNT = 37;
const POCKET_ARC   = (2 * Math.PI) / POCKET_COUNT;
const DEFLECTOR_COUNT = 12;

const P = {
  rimOuter:     1.00,
  rimInner:     0.864,
  trackOuter:   0.864,
  trackCenter:  0.837,
  trackInner:   0.807,
  deflectorPos: 0.791,
  deflectorSize: 0.030,
  pocketOuter:  0.748,
  pocketInner:  0.498,
  numberPos:    0.621,
  innerRingOut: 0.498,
  innerRingIn:  0.476,
  coneOuter:    0.476,
  coneInner:    0.122,
  capRadius:    0.122,
  ballRadius:   0.027,
};

const C = {
  redDeep:    '#5a0000',
  red:        '#C62828',
  redLight:   '#E53935',
  blackDeep:  '#040404',
  black:      '#161616',
  greenDeep:  '#0a3312',
  green:      '#1B5E20',
  greenLight: '#2E7D32',
  wood1:      '#2C1404',
  wood2:      '#5C2D0E',
  wood3:      '#8B5828',
  wood4:      '#B07840',
  gold:       '#D4A535',
  goldDark:   '#8B6914',
  goldLight:  '#F5D060',
  goldGlow:   '#FFF4B0',
  ivory:      '#FFFFF0',
  ivoryShade: '#EAE0C8',
};

function easeOutQuart(t) { return 1 - Math.pow(1 - t, 4); }
function easeInOutCubic(t) {
  return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2;
}
function normalizeAngle(a) {
  return ((a % (Math.PI * 2)) + Math.PI * 3) % (Math.PI * 2) - Math.PI;
}

function createAudioContext() {
  try { return new (window.AudioContext || window.webkitAudioContext)(); }
  catch { return null; }
}
function playTick(audioCtx, volume = 0.03) {
  if (!audioCtx) return;
  try {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain); gain.connect(audioCtx.destination);
    osc.type = 'sine'; osc.frequency.value = 600 + Math.random() * 200;
    gain.gain.setValueAtTime(volume, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.04);
    osc.start(); osc.stop(audioCtx.currentTime + 0.04);
  } catch {}
}
function playWinSound(audioCtx) {
  if (!audioCtx) return;
  try {
    [523, 659, 784].forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain); gain.connect(audioCtx.destination);
      osc.type = 'sine'; osc.frequency.value = freq;
      const t = audioCtx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0.08, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      osc.start(t); osc.stop(t + 0.3);
    });
  } catch {}
}

// ── Drawing ───────────────────────────────────────────────────────────────────

function drawOuterRim(ctx, R) {
  const grad = ctx.createRadialGradient(
    -R * 0.15, -R * 0.15, R * P.rimInner * 0.45,
    0, 0, R * P.rimOuter
  );
  grad.addColorStop(0,    C.wood4);
  grad.addColorStop(0.12, C.wood3);
  grad.addColorStop(0.42, C.wood2);
  grad.addColorStop(0.75, C.wood1);
  grad.addColorStop(1,    '#160702');
  ctx.beginPath();
  ctx.arc(0, 0, R * P.rimOuter, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.save();
  ctx.globalAlpha = 0.055;
  for (let i = 0; i < 90; i++) {
    const a = (i / 90) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * R * P.rimInner,       Math.sin(a) * R * P.rimInner);
    ctx.lineTo(Math.cos(a) * R * P.rimOuter * 0.98, Math.sin(a) * R * P.rimOuter * 0.98);
    ctx.strokeStyle = i % 3 === 0 ? C.wood4 : '#000';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.restore();

  const bevel = ctx.createRadialGradient(0, 0, R * 0.90, 0, 0, R * P.rimOuter);
  bevel.addColorStop(0, 'rgba(0,0,0,0)');
  bevel.addColorStop(1, 'rgba(0,0,0,0.58)');
  ctx.beginPath();
  ctx.arc(0, 0, R * P.rimOuter, 0, Math.PI * 2);
  ctx.fillStyle = bevel;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, 0, R * P.rimOuter, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.lineWidth = R * 0.013;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(0, 0, R * P.rimInner + R * 0.007, 0, Math.PI * 2);
  ctx.strokeStyle = C.goldLight;
  ctx.lineWidth = R * 0.003;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(0, 0, R * P.rimInner, 0, Math.PI * 2);
  ctx.strokeStyle = C.gold;
  ctx.lineWidth = R * 0.009;
  ctx.stroke();

  const midR = R * (P.rimInner + (1 - P.rimInner) * 0.48);
  ctx.beginPath();
  ctx.arc(0, 0, midR, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(175,125,45,0.20)';
  ctx.lineWidth = R * 0.018;
  ctx.stroke();
}

function drawBallTrack(ctx, R) {
  const grad = ctx.createRadialGradient(0, 0, R * P.trackInner, 0, 0, R * P.trackOuter);
  grad.addColorStop(0,   '#464646');
  grad.addColorStop(0.2, '#888888');
  grad.addColorStop(0.5, '#ABABAB');
  grad.addColorStop(0.8, '#888888');
  grad.addColorStop(1,   '#555555');
  ctx.beginPath();
  ctx.arc(0, 0, R * P.trackOuter, 0, Math.PI * 2);
  ctx.arc(0, 0, R * P.trackInner, 0, Math.PI * 2, true);
  ctx.fillStyle = grad;
  ctx.fill();

  const hlA = -Math.PI * 0.65, hlW = Math.PI * 0.38;
  ctx.beginPath();
  ctx.arc(0, 0, R * P.trackOuter, hlA - hlW, hlA + hlW);
  ctx.arc(0, 0, R * P.trackInner, hlA + hlW, hlA - hlW, true);
  ctx.closePath();
  ctx.fillStyle = 'rgba(255,255,255,0.11)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, 0, R * P.trackOuter, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, 0, R * P.trackInner, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(0,0,0,0.42)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

function drawDeflectors(ctx, R) {
  const pos  = R * P.deflectorPos;
  const size = R * P.deflectorSize;
  for (let i = 0; i < DEFLECTOR_COUNT; i++) {
    const angle = (i / DEFLECTOR_COUNT) * Math.PI * 2;
    ctx.save();
    ctx.rotate(angle);
    ctx.translate(pos, 0);

    ctx.save();
    ctx.translate(size * 0.18, size * 0.18);
    ctx.beginPath();
    ctx.moveTo(0, -size * 1.1); ctx.lineTo(size * 0.62, 0);
    ctx.lineTo(0,  size * 1.1); ctx.lineTo(-size * 0.62, 0);
    ctx.closePath();
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fill();
    ctx.restore();

    const dg = ctx.createLinearGradient(-size * 0.55, -size, size * 0.55, size);
    dg.addColorStop(0,    C.goldGlow);
    dg.addColorStop(0.28, C.goldLight);
    dg.addColorStop(0.62, C.gold);
    dg.addColorStop(1,    C.goldDark);
    ctx.beginPath();
    ctx.moveTo(0, -size * 1.1); ctx.lineTo(size * 0.62, 0);
    ctx.lineTo(0,  size * 1.1); ctx.lineTo(-size * 0.62, 0);
    ctx.closePath();
    ctx.fillStyle = dg;
    ctx.fill();
    ctx.strokeStyle = C.goldDark;
    ctx.lineWidth = 0.7;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, -size * 1.1);
    ctx.lineTo(-size * 0.62, 0);
    ctx.lineTo(0, 0);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,255,255,0.30)';
    ctx.fill();

    ctx.restore();
  }
}

function drawPockets(ctx, R) {
  const outerR = R * P.pocketOuter;
  const innerR = R * P.pocketInner;

  for (let i = 0; i < POCKET_COUNT; i++) {
    const startAngle = i * POCKET_ARC - POCKET_ARC / 2;
    const endAngle   = startAngle + POCKET_ARC;
    const n = NUMBERS[i];
    ctx.beginPath();
    ctx.arc(0, 0, outerR, startAngle, endAngle);
    ctx.arc(0, 0, innerR, endAngle, startAngle, true);
    ctx.closePath();

    const g = ctx.createRadialGradient(0, 0, innerR * 0.82, 0, 0, outerR * 1.02);
    if (n === 0) {
      g.addColorStop(0,    C.greenDeep);
      g.addColorStop(0.38, C.green);
      g.addColorStop(0.72, C.greenLight);
      g.addColorStop(1,    C.green);
    } else if (RED_NUMBERS.has(n)) {
      g.addColorStop(0,    C.redDeep);
      g.addColorStop(0.38, C.red);
      g.addColorStop(0.72, C.redLight);
      g.addColorStop(1,    C.red);
    } else {
      g.addColorStop(0,    C.blackDeep);
      g.addColorStop(0.38, C.black);
      g.addColorStop(0.72, '#222222');
      g.addColorStop(1,    C.black);
    }
    ctx.fillStyle = g;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(0, 0, innerR + R * 0.020, startAngle, endAngle);
    ctx.arc(0, 0, innerR,             endAngle, startAngle, true);
    ctx.closePath();
    ctx.fillStyle = 'rgba(0,0,0,0.48)';
    ctx.fill();
  }

  const fw = R * 0.0052;
  for (let i = 0; i < POCKET_COUNT; i++) {
    const angle = i * POCKET_ARC - POCKET_ARC / 2;
    ctx.save();
    ctx.rotate(angle);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(innerR, -fw - R * 0.001, outerR - innerR, fw * 2 + R * 0.002);
    ctx.fillStyle = C.gold;
    ctx.fillRect(innerR, -fw, outerR - innerR, fw * 2);
    ctx.fillStyle = C.goldLight;
    ctx.fillRect(innerR + R * 0.01, -fw, outerR - innerR - R * 0.014, fw * 0.5);
    ctx.restore();
  }

  ctx.beginPath();
  ctx.arc(0, 0, outerR, 0, Math.PI * 2);
  ctx.strokeStyle = C.gold;
  ctx.lineWidth = R * 0.007;
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(0, 0, innerR, 0, Math.PI * 2);
  ctx.strokeStyle = C.gold;
  ctx.lineWidth = R * 0.007;
  ctx.stroke();
}

function drawNumbers(ctx, R) {
  const numR     = R * P.numberPos;
  const fontSize = R * 0.065;
  ctx.font         = `bold ${fontSize}px "Georgia", serif`;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  for (let i = 0; i < POCKET_COUNT; i++) {
    const angle = i * POCKET_ARC;
    ctx.save();
    ctx.rotate(angle);
    ctx.translate(numR, 0);
    ctx.rotate(Math.PI / 2);
    ctx.lineWidth   = R * 0.006;
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    ctx.strokeText(NUMBERS[i].toString(), 0, 0);
    ctx.fillStyle   = '#FFFFFF';
    ctx.shadowColor = 'rgba(0,0,0,0.9)';
    ctx.shadowBlur  = 4;
    ctx.fillText(NUMBERS[i].toString(), 0, 0);
    ctx.shadowBlur  = 0;
    ctx.restore();
  }
}

function drawInnerRing(ctx, R) {
  const outerR = R * P.innerRingOut;
  const innerR = R * P.innerRingIn;
  const grad = ctx.createRadialGradient(-outerR * 0.2, -outerR * 0.2, 0, 0, 0, outerR);
  grad.addColorStop(0,   C.goldGlow);
  grad.addColorStop(0.3, C.goldLight);
  grad.addColorStop(0.6, C.gold);
  grad.addColorStop(1,   C.goldDark);
  ctx.beginPath();
  ctx.arc(0, 0, outerR, 0, Math.PI * 2);
  ctx.arc(0, 0, innerR, 0, Math.PI * 2, true);
  ctx.fillStyle = grad;
  ctx.fill();
}

function drawCone(ctx, R) {
  const outerR = R * P.coneOuter;
  const innerR = R * P.coneInner;
  const grad = ctx.createRadialGradient(-R * 0.06, -R * 0.06, innerR * 0.3, 0, 0, outerR);
  grad.addColorStop(0,    '#2A7A42');
  grad.addColorStop(0.30, '#1E6B33');
  grad.addColorStop(0.62, '#126028');
  grad.addColorStop(1,    '#083018');
  ctx.beginPath();
  ctx.arc(0, 0, outerR, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  [0.93, 0.78, 0.62, 0.45].forEach((f, idx) => {
    ctx.beginPath();
    ctx.arc(0, 0, outerR * f, 0, Math.PI * 2);
    ctx.strokeStyle = idx % 2 === 0 ? 'rgba(212,165,53,0.20)' : 'rgba(212,165,53,0.10)';
    ctx.lineWidth = R * 0.004;
    ctx.stroke();
  });
  ctx.save();
  ctx.globalAlpha = 0.09;
  for (let i = 0; i < 18; i++) {
    const a = (i / 18) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * innerR * 1.5, Math.sin(a) * innerR * 1.5);
    ctx.lineTo(Math.cos(a) * outerR * 0.9,  Math.sin(a) * outerR * 0.9);
    ctx.strokeStyle = C.gold;
    ctx.lineWidth   = 0.8;
    ctx.stroke();
  }
  ctx.restore();
}

function drawCenterCap(ctx, R) {
  const r = R * P.capRadius;
  const ringGrad = ctx.createRadialGradient(-r * 0.22, -r * 0.22, r * 0.3, 0, 0, r);
  ringGrad.addColorStop(0,    C.goldGlow);
  ringGrad.addColorStop(0.25, C.goldLight);
  ringGrad.addColorStop(0.55, C.gold);
  ringGrad.addColorStop(1,    C.goldDark);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = ringGrad;
  ctx.fill();
  [0.94, 0.87, 0.80].forEach((f, idx) => {
    ctx.beginPath();
    ctx.arc(0, 0, r * f, 0, Math.PI * 2);
    ctx.strokeStyle = idx === 1 ? 'rgba(255,240,160,0.28)' : 'rgba(0,0,0,0.28)';
    ctx.lineWidth = R * 0.003;
    ctx.stroke();
  });
  const innerGrad = ctx.createRadialGradient(-r * 0.12, -r * 0.16, 0, 0, 0, r * 0.70);
  innerGrad.addColorStop(0,   '#353535');
  innerGrad.addColorStop(0.5, '#1a1a1a');
  innerGrad.addColorStop(1,   '#060606');
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.70, 0, Math.PI * 2);
  ctx.fillStyle = innerGrad;
  ctx.fill();
  const pinGrad = ctx.createRadialGradient(-r * 0.04, -r * 0.04, 0, 0, 0, r * 0.12);
  pinGrad.addColorStop(0, C.goldGlow);
  pinGrad.addColorStop(1, C.gold);
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.12, 0, Math.PI * 2);
  ctx.fillStyle = pinGrad;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-r * 0.22, -r * 0.28, r * 0.30, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.11)';
  ctx.fill();
}

function drawIndicator(ctx, R) {
  const y    = -R * P.trackInner + R * 0.01;
  const size = R * 0.04;
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur  = 5;
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(-size * 0.6, y - size * 1.2);
  ctx.lineTo( size * 0.6, y - size * 1.2);
  ctx.closePath();
  const grad = ctx.createLinearGradient(0, y, 0, y - size * 1.2);
  grad.addColorStop(0, C.goldLight);
  grad.addColorStop(1, C.gold);
  ctx.fillStyle   = grad;
  ctx.fill();
  ctx.strokeStyle = C.goldDark;
  ctx.lineWidth   = 1;
  ctx.stroke();
  ctx.shadowBlur  = 0;
  ctx.restore();
}

function drawBall(ctx, R, angle, radius, glowIntensity = 0) {
  const r = R * P.ballRadius;
  const x = Math.cos(angle) * radius * R;
  const y = Math.sin(angle) * radius * R;
  ctx.save();
  if (glowIntensity > 0) {
    ctx.shadowColor = `rgba(255,255,210,${glowIntensity * 0.7})`;
    ctx.shadowBlur  = r * 5 * glowIntensity;
  }
  ctx.beginPath();
  ctx.arc(x + r * 0.28, y + r * 0.28, r, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(0,0,0,0.38)';
  ctx.fill();
  const ballGrad = ctx.createRadialGradient(x - r * 0.32, y - r * 0.32, r * 0.08, x, y, r);
  ballGrad.addColorStop(0,    '#FFFFFF');
  ballGrad.addColorStop(0.35, C.ivory);
  ballGrad.addColorStop(0.8,  C.ivoryShade);
  ballGrad.addColorStop(1,    '#C8C0A8');
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = ballGrad;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x - r * 0.28, y - r * 0.28, r * 0.36, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.78)';
  ctx.fill();
  ctx.restore();
}

// ── Exported: deterministic spin params ──────────────────────────────────────

export function calculateSpinParams(targetNumber, currentWheelAngle = 0, currentBallAngle = -Math.PI / 2) {
  const pocketIndex = NUMBERS.indexOf(targetNumber);
  if (pocketIndex === -1) return null;
  const indicatorAngle = -Math.PI / 2;
  const pocketAngle    = pocketIndex * POCKET_ARC;
  let targetWheelAngle = indicatorAngle - pocketAngle;
  const minRotations   = 5 + Math.random() * 4;
  const currentAngle   = currentWheelAngle % (Math.PI * 2);
  let diff = targetWheelAngle - currentAngle;
  diff = ((diff % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const totalWheelRotation = diff + minRotations * Math.PI * 2;
  const finalWheelAngle    = currentWheelAngle + totalWheelRotation;
  const finalPocketScreen  = pocketIndex * POCKET_ARC + finalWheelAngle;
  const randomOffset       = (Math.random() - 0.5) * 6 * POCKET_ARC;
  const rawBallDiff        = ((finalPocketScreen + randomOffset - currentBallAngle) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
  const ballSpins          = 8 + Math.floor(Math.random() * 5);
  const totalBallRotation  = rawBallDiff - ballSpins * Math.PI * 2;
  const animDuration       = 8000 + Math.random() * 3000;
  return {
    number: targetNumber,
    animDuration,
    totalWheelRotation,
    totalBallRotation,
    startWheelAngle: currentWheelAngle,
    startBallAngle:  currentBallAngle,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

const RouletteWheel = React.forwardRef(function RouletteWheel(
  { targetNumber, spinning, onSpinComplete, onPocketPass, spinParams, timeOffset = 0 },
  ref
) {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const audioCtxRef  = useRef(null);
  const rafRef       = useRef(null);
  const sizeRef      = useRef(500);
  const callbacksRef = useRef({ onSpinComplete, onPocketPass, targetNumber });
  callbacksRef.current = { onSpinComplete, onPocketPass, targetNumber };

  const state = useRef({
    wheelAngle: 0,
    ballAngle:  -Math.PI / 2,
    ballRadius: P.trackCenter,
    animating:  false,
    animStartTime: 0,
    animDuration:  0,
    startWheelAngle:    0,
    totalWheelRotation: 0,
    startBallAngle:     0,
    totalBallRotation:  0,
    targetPocketIndex:  0,
    lastTickPocket: -1,
    settled:        false,
    glowIntensity:  0,
  });

  const ensureAudio = useCallback(() => {
    if (!audioCtxRef.current) audioCtxRef.current = createAudioContext();
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume();
  }, []);

  useEffect(() => {
    if (!spinning || targetNumber == null) return;
    ensureAudio();
    const s = state.current;
    const pocketIndex = NUMBERS.indexOf(targetNumber);
    if (pocketIndex === -1) return;
    if (spinParams) {
      s.animDuration       = spinParams.animDuration;
      s.startWheelAngle    = spinParams.startWheelAngle;
      s.totalWheelRotation = spinParams.totalWheelRotation;
      s.startBallAngle     = spinParams.startBallAngle;
      s.totalBallRotation  = spinParams.totalBallRotation;
    } else {
      const indicatorAngle = -Math.PI / 2;
      const pocketAngle    = pocketIndex * POCKET_ARC;
      let targetWheelAngle = indicatorAngle - pocketAngle;
      const minRotations   = 5 + Math.random() * 4;
      const currentAngle   = s.wheelAngle % (Math.PI * 2);
      let diff = targetWheelAngle - currentAngle;
      diff = ((diff % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const totalWheelRotation = diff + minRotations * Math.PI * 2;
      const finalWheelAngle    = s.wheelAngle + totalWheelRotation;
      const finalPocketScreen  = pocketIndex * POCKET_ARC + finalWheelAngle;
      const randomOffset       = (Math.random() - 0.5) * 6 * POCKET_ARC;
      const rawBallDiff        = ((finalPocketScreen + randomOffset - s.ballAngle) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const ballSpins          = 8 + Math.floor(Math.random() * 5);
      s.animDuration       = 8000 + Math.random() * 3000;
      s.startWheelAngle    = s.wheelAngle;
      s.totalWheelRotation = totalWheelRotation;
      s.startBallAngle     = s.ballAngle;
      s.totalBallRotation  = rawBallDiff - ballSpins * Math.PI * 2;
    }
    s.animStartTime     = performance.now() - timeOffset;
    s.targetPocketIndex = pocketIndex;
    s.animating         = true;
    s.settled           = false;
    s.glowIntensity     = 0;
    s.lastTickPocket    = -1;
  }, [spinning, targetNumber, ensureAudio, spinParams, timeOffset]);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx  = canvas.getContext('2d');
    const size = sizeRef.current;
    const dpr  = window.devicePixelRatio || 1;
    const R    = (size / 2) * 0.97;
    const cx   = size / 2;
    const cy   = size / 2;
    const s    = state.current;

    if (s.animating) {
      const now     = performance.now();
      const elapsed = now - s.animStartTime;
      const rawT    = Math.min(elapsed / s.animDuration, 1);
      const t       = easeOutQuart(rawT);
      s.wheelAngle  = s.startWheelAngle + s.totalWheelRotation * t;
      const ballT   = easeOutQuart(Math.min(rawT * 1.1, 1));
      const dropStart = 0.72, dropEnd = 0.88;
      let dropProgress = 0;
      if (rawT > dropStart) {
        dropProgress = Math.min((rawT - dropStart) / (dropEnd - dropStart), 1);
        dropProgress = easeInOutCubic(dropProgress);
      }
      s.ballRadius = P.trackCenter - (P.trackCenter - P.numberPos) * dropProgress;
      if (rawT > dropEnd && rawT < 1) {
        const bounceT = (rawT - dropEnd) / (1 - dropEnd);
        s.ballRadius += Math.sin(bounceT * Math.PI * 4) * Math.exp(-bounceT * 5) * 0.02;
      }
      if (rawT < dropStart) {
        s.ballAngle = s.startBallAngle + s.totalBallRotation * ballT;
      } else {
        const lockT = easeInOutCubic(Math.min((rawT - dropStart) / (dropEnd - dropStart), 1));
        const freeBallAngle     = s.startBallAngle + s.totalBallRotation * ballT;
        const pocketScreenAngle = s.targetPocketIndex * POCKET_ARC + s.wheelAngle;
        let angleDiff = pocketScreenAngle - freeBallAngle;
        angleDiff     = normalizeAngle(angleDiff);
        s.ballAngle   = freeBallAngle + angleDiff * lockT;
      }
      if (rawT > 0.9) s.glowIntensity = (rawT - 0.9) / 0.1;
      if (rawT < 0.72) {
        const cur = Math.floor(
          (((-s.ballAngle + s.wheelAngle) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2)) / POCKET_ARC
        ) % POCKET_COUNT;
        if (cur !== s.lastTickPocket) {
          s.lastTickPocket = cur;
          playTick(audioCtxRef.current, 0.02 + (1 - rawT) * 0.03);
          if (callbacksRef.current.onPocketPass) callbacksRef.current.onPocketPass();
        }
      }
      if (rawT >= 1 && !s.settled) {
        s.settled       = true;
        s.animating     = false;
        s.glowIntensity = 1;
        s.wheelAngle    = s.startWheelAngle + s.totalWheelRotation;
        s.ballRadius    = P.numberPos;
        s.ballAngle     = s.targetPocketIndex * POCKET_ARC + s.wheelAngle;
        playWinSound(audioCtxRef.current);
        setTimeout(() => {
          const cb = callbacksRef.current;
          if (cb.onSpinComplete) cb.onSpinComplete(cb.targetNumber);
        }, 400);
      }
    }

    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    ctx.translate(cx, cy);

    const bgGlow = ctx.createRadialGradient(0, 0, R * 0.1, 0, 0, R * 1.12);
    bgGlow.addColorStop(0,   'rgba(30,100,55,0.22)');
    bgGlow.addColorStop(0.5, 'rgba(15,60,30,0.10)');
    bgGlow.addColorStop(1,   'rgba(0,0,0,0)');
    ctx.beginPath();
    ctx.arc(0, 0, R * 1.12, 0, Math.PI * 2);
    ctx.fillStyle = bgGlow;
    ctx.fill();

    drawOuterRim(ctx, R);
    drawBallTrack(ctx, R);
    drawDeflectors(ctx, R);

    ctx.save();
    ctx.rotate(s.wheelAngle);
    drawPockets(ctx, R);
    drawNumbers(ctx, R);
    drawInnerRing(ctx, R);
    drawCone(ctx, R);
    drawCenterCap(ctx, R);
    ctx.restore();

    drawIndicator(ctx, R);
    drawBall(ctx, R, s.ballAngle, s.ballRadius, s.glowIntensity);

    ctx.restore();
    rafRef.current = requestAnimationFrame(render);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const canvas    = canvasRef.current;
    if (!container || !canvas) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const size = Math.min(width, height);
        sizeRef.current = size;
        const dpr = window.devicePixelRatio || 1;
        canvas.width        = size * dpr;
        canvas.height       = size * dpr;
        canvas.style.width  = size + 'px';
        canvas.style.height = size + 'px';
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(render);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [render]);

  return (
    <div ref={containerRef} style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <canvas ref={canvasRef} />
    </div>
  );
});

export default RouletteWheel;
